import React, { useEffect, useCallback } from 'react';
import queryString from 'query-string';
import { useHistory } from 'react-router-dom';

import { LEGACY_DIALOG_KEYS } from 'modals/keys';
import { useDialogStore } from 'hooks/dialogStore/useDialogStore';
import AuthContext from '../AuthContext';
import {
  SHOW_WELCOME_URL_PARAM,
  SUBMITTER_USER_ID_PARAM,
} from '../../Router/onboarding/constants';

const QUERY_PARAM = SHOW_WELCOME_URL_PARAM;

export type OnboardingWelcomeModalContextType = {
  visible: boolean;
  openModal: () => void;
  closeModal: () => void;
  queryParam: typeof QUERY_PARAM;
  submitterQueryParam: typeof SUBMITTER_USER_ID_PARAM;
  /** The flow is to show the welcome modal, then depending on the
   * role/stage of onboarding display the wizard or the prelaunch modal
   * Upon closing these modals the global banner should display.  */
  isDoneWelcomeFlow: boolean;
  setIsDoneWelcomeFlow: (done: boolean) => void;
};

const OnboardingWelcomeModalContext =
  React.createContext<OnboardingWelcomeModalContextType>(
    {} as OnboardingWelcomeModalContextType,
  );

export const OnboardingWelcomeModalContextProvider: React.FC = ({
  children,
}) => {
  const [visible, setVisible] = React.useState(false);
  const [isDoneWelcomeFlow, setIsDoneWelcomeFlow] = React.useState(true);

  const { addDialog, removeDialog, isDialogVisible, isUsingNewArchitecture } =
    useDialogStore();
  const usingNewModalArch = isUsingNewArchitecture(
    LEGACY_DIALOG_KEYS.OnboardingWelcomeModal,
  );
  const isVisible = isDialogVisible(LEGACY_DIALOG_KEYS.OnboardingWelcomeModal);

  const { auth, logout, upgradePlanDataLoaded, isUnclaimedOnAgentPlan } =
    React.useContext(AuthContext);

  const history = useHistory();
  const query = queryString.parse(history.location.search);

  const openModal: OnboardingWelcomeModalContextType['openModal'] =
    useCallback(() => {
      if (isUnclaimedOnAgentPlan || isVisible) return;

      if (usingNewModalArch) {
        addDialog({ key: LEGACY_DIALOG_KEYS.OnboardingWelcomeModal });
      } else {
        setVisible(true);
      }
      setIsDoneWelcomeFlow(false);
    }, [addDialog, isUnclaimedOnAgentPlan, isVisible, usingNewModalArch]);

  const closeModal: OnboardingWelcomeModalContextType['closeModal'] =
    useCallback(() => {
      const queryParams = new URLSearchParams(history.location.search);
      queryParams.delete(QUERY_PARAM);
      queryParams.delete(SUBMITTER_USER_ID_PARAM);
      if (queryParams.get('sso')) {
        queryParams.delete('sso');
      }
      history.replace({ search: queryParams.toString() });

      if (usingNewModalArch) {
        removeDialog(LEGACY_DIALOG_KEYS.OnboardingWelcomeModal);
      } else {
        setVisible(false);
      }
    }, [history, removeDialog, usingNewModalArch]);

  useEffect(() => {
    const state = query?.[QUERY_PARAM]?.toString();
    const submitterId = query?.[SUBMITTER_USER_ID_PARAM]?.toString();
    if (submitterId && submitterId !== auth!.id) {
      // This is for Org onboarding, claimed submitter case. If the user is logged into the wrong account, log them out
      logout();
      return;
    }

    if (state === 'true') {
      if (!upgradePlanDataLoaded) return;
      openModal();
    }
  }, [query, openModal, logout, upgradePlanDataLoaded, auth]);

  return (
    <OnboardingWelcomeModalContext.Provider
      value={{
        visible: usingNewModalArch
          ? isDialogVisible(LEGACY_DIALOG_KEYS.OnboardingWelcomeModal)
          : visible,
        openModal,
        closeModal,
        queryParam: QUERY_PARAM,
        isDoneWelcomeFlow,
        setIsDoneWelcomeFlow,
        submitterQueryParam: SUBMITTER_USER_ID_PARAM,
      }}
    >
      {children}
    </OnboardingWelcomeModalContext.Provider>
  );
};

export default OnboardingWelcomeModalContext;
